import { useState, useRef, useEffect, useMemo, useCallback, memo } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, CartesianGrid } from 'recharts';
import { ChartContainer, ChartTooltip } from '../../components/ui/chart';

const CustomTooltipContent = ({ active, payload, label, config }) => {
  if (active && payload && payload.length) {
    return (
      <div
        className="rounded-lg border p-3 text-sm shadow-md"
        style={{
          background: 'var(--q3-surface-default)',
          borderColor: 'var(--q3-stroke-normal)',
          minWidth: '200px',
        }}
      >
        <div className="font-bold mb-2" style={{ color: 'var(--q3-neutral-default)' }}>
          {label}
        </div>
        <div className="space-y-2">
          {payload.map((entry, index) => (
            <div key={`item-${index}`} className="flex items-center justify-between gap-x-4">
              <div className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 shrink-0 rounded-full" style={{ backgroundColor: entry.color }} />
                <span style={{ color: 'var(--q3-neutral-light)' }}>{config?.[entry.name]?.label || entry.name}</span>
              </div>
              <span className="font-bold" style={{ color: 'var(--q3-neutral-default)' }}>
                {entry.value}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  }
  return null;
};

const MistakesLineChart = ({ title, description, data, expanded, onToggleExpand, lines, xAxisDataKey }) => {
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [hoveredLine, setHoveredLine] = useState(null);
  const [visibleRange, setVisibleRange] = useState({ start: 0, end: 10 });
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const chartContainerRef = useRef(null);
  
  // reversing the api reponse
  const reversedData = useMemo(() => {
    return [...data].reverse();
  }, [data]);

 
  useEffect(() => {
    const checkViewport = () => {
      const width = window.innerWidth;
      const mobile = width < 768;
      const tablet = width >= 768 && width < 1024;
      
      setIsMobile(mobile);
      setIsTablet(tablet);
      
      let itemsPerView;
      if (mobile) {
        itemsPerView = 3;
      } else if (tablet) {
        itemsPerView = 6;
      } else {
        itemsPerView = 10;
      }

      if (reversedData.length > 0) {
     
        setVisibleRange({
          start: Math.max(0, reversedData.length - itemsPerView),
          end: reversedData.length,
        });
      }
    };

    checkViewport();
    window.addEventListener('resize', checkViewport);
    return () => window.removeEventListener('resize', checkViewport);
  }, [reversedData.length]);

  
  const handleScroll = useCallback(
    (direction) => {
      let itemsPerView;
      if (isMobile) {
        itemsPerView = 3;
      } else if (isTablet) {
        itemsPerView = 6;
      } else {
        itemsPerView = 10;
      }
      
      const total = reversedData.length;

      setVisibleRange((prev) => {
        if (direction === 'left') {
          const newStart = Math.max(0, prev.start - 1);
          const newEnd = Math.max(itemsPerView, prev.end - 1);
          return {
            start: newStart,
            end: newEnd,
          };
        } else {
          const newStart = Math.min(total - itemsPerView, prev.start + 1);
          const newEnd = Math.min(total, prev.end + 1);
          return {
            start: newStart,
            end: newEnd,
          };
        }
      });
    },
    [isMobile, isTablet, reversedData.length],
  );

 
  const handleWheel = useCallback(
    (e) => {
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
        if (e.deltaX > 0) handleScroll('right');
        else handleScroll('left');
      }
    },
    [handleScroll],
  );


  const touchStartX = useRef(null);
  
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = useCallback(
    (e) => {
      if (!touchStartX.current) return;
      const diff = e.touches[0].clientX - touchStartX.current;

      if (Math.abs(diff) > 20) {
        if (diff > 0) handleScroll('left');
        else handleScroll('right');
        touchStartX.current = null;
      }
    },
    [handleScroll],
  );

  const chartConfig = lines.reduce((acc, line) => {
    acc[line.dataKey] = { label: line.label, color: line.color };
    return acc;
  }, {});

  const visibleData = useMemo(() => {
    return reversedData.slice(visibleRange.start, visibleRange.end).map((item, index) => ({
      ...item,
      displayIndex: visibleRange.start + index + 1,
    }));
  }, [reversedData, visibleRange]);

 const processedData = useMemo(() => {
  const shortenLabel = (label) => {
    if (typeof label !== 'string') return label;

    // Existing logic for Test (...) - number
    if (label.includes('Test')) {
      const acronymMatch = label.match(/\(([^)]+)\)/);
      const numberMatch = label.match(/-\s*(\d+)$/);

      if (acronymMatch && numberMatch) {
        label = `${acronymMatch[1]}-${numberMatch[1]}`;
      }
    }

   
    const MAX_LENGTH = 12;
    if (label.length > MAX_LENGTH) {
      return label.substring(0, MAX_LENGTH) + "...";
    }

    return label;
  };

  return visibleData.map((item) => ({
    ...item,
    [xAxisDataKey]: shortenLabel(item[xAxisDataKey]),
    shortTitle: `Test ${item.displayIndex}`,
  }));
}, [visibleData, xAxisDataKey]);


 
  const isLeftDisabled = visibleRange.start === 0;
  const isRightDisabled = visibleRange.end >= reversedData.length;

  return (
    <Card className="mb-6 py-3 gap-2 shadow-none">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-lg font-bold text-[var(--q3-neutral-default)]">{title}</CardTitle>
        <div className="flex items-center gap-2">
         
          <div className="flex gap-2 mr-3">
            <button
              onClick={() => handleScroll('left')}
              disabled={isLeftDisabled}
              className={`p-2 rounded-[6.667px] border border-[var(--q3-stroke-normal)]
                bg-[var(--q3-surface-default)] transition-all cursor-pointer 
                ${isLeftDisabled ? 'text-gray-300 cursor-not-allowed' : 'text-[var(--q3-neutral-default)] hover:bg-[var(--q3-surface-dim)]'}`}
            >
              <ChevronDown className="w-4 h-4 rotate-90" color={isLeftDisabled ? '#d1d5db' : 'var(--q3-neutral-default)'} />
            </button>

            <button
              onClick={() => handleScroll('right')}
              disabled={isRightDisabled}
              className={`p-2 rounded-[6.667px] border border-[var(--q3-stroke-normal)]
                bg-[var(--q3-surface-default)] transition-all cursor-pointer
                ${isRightDisabled ? 'text-gray-300 cursor-not-allowed' : 'text-[var(--q3-neutral-default)] hover:bg-[var(--q3-surface-dim)]'}`}
            >
              <ChevronDown className="w-4 h-4 -rotate-90" color={isRightDisabled ? '#d1d5db' : 'var(--q3-neutral-default)'} />
            </button>
          </div>
          
          <Button
            variant="ghost"
            size="sm"
            onClick={onToggleExpand}
            className="rounded-full border text-[var(--q3-accent-normal)]"
          >
            {expanded ? 'HIDE' : 'VIEW'}
            {expanded ? <ChevronUp className="h-4 w-4 ml-1" /> : <ChevronDown className="h-4 w-4 ml-1" />}
          </Button>
        </div>
      </CardHeader>
      {expanded && (
        <CardContent className="p-0">
          <div className="px-6">
            <p className="text-sm text-[var(--q3-neutral-light)] mb-4">{description}</p>
            <div className="flex flex-wrap items-center gap-4 mb-4">
              {lines.map((line) => (
                <div key={line.dataKey} className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: line.color }} />
                  <span className="text-sm text-[var(--q3-neutral-default)]">{line.label}</span>
                </div>
              ))}
            </div>
          </div>
          <div 
            ref={chartContainerRef}
            className="h-60 w-full overflow-x-auto"
            onWheel={handleWheel}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
          >
            <div style={{ width: '100%', minWidth: '600px', height: '100%' }}>
              <ChartContainer config={chartConfig} className="h-full w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={processedData}
                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                    onMouseMove={(state) => {
                      if (state && state.activeTooltipIndex !== undefined) {
                        setHoveredIndex(state.activeTooltipIndex);
                      }
                    }}
                    onMouseLeave={() => {
                      setHoveredIndex(null);
                      setHoveredLine(null);
                    }}
                  >
                    <CartesianGrid stroke="var(--q3-stroke-normal)" horizontal={true} vertical={true} />
                    <XAxis
                      dataKey={xAxisDataKey}
                      axisLine={false}
                      tickLine={false}
                      height={60}
                      interval={0}
                      tick={{ fontSize: 12, fill: 'var(--q3-neutral-light)' }}
                    />
                    <YAxis 
                      axisLine={false} 
                      tickLine={false} 
                      tick={{ fontSize: 12, fill: 'var(--q3-neutral-light)' }} 
                    />
                    <ChartTooltip content={<CustomTooltipContent config={chartConfig} />} />
                    {lines.map((line) => (
                      <Line
                        isAnimationActive={false}
                        key={line.dataKey}
                        type="monotone"
                        dataKey={line.dataKey}
                        stroke={line.color}
                        strokeWidth={2}
                        dot={false}
                        activeDot={{
                          fill: line.color,
                          strokeWidth: 2,
                          r: 4,
                          style: { display: hoveredLine === line.dataKey || hoveredLine === null ? 'block' : 'none' },
                        }}
                        onMouseEnter={() => setHoveredLine(line.dataKey)}
                        onMouseLeave={() => setHoveredLine(null)}
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </ChartContainer>
            </div>
          </div>
        </CardContent>
      )}
    </Card>
  );
};

export default memo(MistakesLineChart);